import React, { useLayoutEffect, useRef, useEffect } from "react";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Fade from "@mui/material/Fade";
import Grow from "@mui/material/Grow";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import PsychologyRoundedIcon from "@mui/icons-material/PsychologyRounded";
import TableRowsRoundedIcon from "@mui/icons-material/TableRowsRounded";

import {
  invokeBedrockInlineAgent,
  getQueryResults,
  generateChart,
} from "../utils/AwsCalls";
import AnswerDetailsDialog from "./AnswerDetailsDialog.js";
import QueryResultsDisplay from "./QueryResultsDisplay";
import InputQuery from "./InputQuery";
import ChatWelcomeView from "./ChatWelcomeView";

import { configureAutoTrack } from "aws-amplify/analytics";
import { v4 as uuidv4 } from "uuid";

import BrainDialog from "./BrainDialog.js";
import Answering from "./Answering.js";
import MyChart from "./MyChart.js";
import ChatStreamSimulator from "./ChatStreamSimulator.js";
import MarkdownRenderer from "./MarkdownRenderer.js";

import "./Chat.css";

configureAutoTrack({
  enable: true,
  type: "event",
});

const Chat = ({
  userName,
  answers,
  setAnswers,
  setDashboardEnabled,
  language,
  assistant,
}) => {
  const isMounted = useRef(true);

  const [totalAnswers, setTotalAnswers] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [controlAnswers, setControlAnswers] = React.useState([]);
  const [sessionId, setSessionId] = React.useState(uuidv4());
  const [errorMessage, setErrorMessage] = React.useState("");
  const [height, setHeight] = React.useState(480);
  const [size, setSize] = React.useState([0, 0]);
  const [selectedAB, setSelectedAB] = React.useState(0);
  const [openAnswerDetails, setOpenAnswerDetails] = React.useState(false);
  const [openBrain, setOpenBrain] = React.useState(false);

  const [grandTotalInputTokens, setGrandTotalInputTokens] = React.useState(0);
  const [grandTotalOutputTokens, setGrandTotalOutputTokens] = React.useState(0);

  // Standard border radius to use throughout the component
  const borderRadius = 8;

  useEffect(() => {
    // Set mounted to true when component mounts
    isMounted.current = true;

    // Return cleanup function to run on unmount
    return () => {
      isMounted.current = false;
      console.log("Component unmounted");
    };
  }, []);

  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [answers]);

  useLayoutEffect(() => {
    function updateSize() {
      setSize([window.innerWidth, window.innerHeight]);
      const myh = window.innerHeight - 128;
      if (myh < 280) {
        setHeight(280);
      } else {
        setHeight(myh);
      }
    }
    window.addEventListener("resize", updateSize);
    updateSize();
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const effectRan = React.useRef(false);
  useEffect(() => {
    if (!effectRan.current) {
      console.log("effect applied - only on the FIRST mount");
      const fetchData = async () => {
        console.log("Chat");
      };
      fetchData().catch(console.error);
    }
    return () => (effectRan.current = true);
  }, []);

const getAnswer = async (my_query) => {
  if (!loading && my_query !== "") {
    setControlAnswers((prevState) => [...prevState, {}]);
    setAnswers((prevState) => [...prevState, { query: my_query }]);
    setLoading(true);
    setErrorMessage("");

    try {
      const queryUuid = uuidv4();
      const {
        completion,
        usage,
        totalInputTokens,
        totalOutputTokens,
        runningTraces,
        countRationals,
      } = await invokeBedrockInlineAgent(
        sessionId,
        my_query,
        setAnswers,
        setControlAnswers,
        userName,
        queryUuid,
        assistant.instruction.replace(
          "{communication_style}",
          assistant.communication_styles[
            assistant.selected_communication_style
          ].prompt
        ),
        assistant.schema,
        assistant.models[assistant.selected_model].id,
        assistant.assistant_db_name
      );

      if (!isMounted.current) {
        console.log("No moreeeee,...");
        return;
      }

      setGrandTotalInputTokens((prev) => prev + totalInputTokens);
      setGrandTotalOutputTokens((prev) => prev + totalOutputTokens);

      let json = {
        text: completion,
        usage,
        totalInputTokens,
        totalOutputTokens,
        runningTraces,
        queryUuid,
        countRationals,
      };

      const queryResults = await getQueryResults(queryUuid);

      if (!isMounted.current) {
        console.log("No moreeeee,...");
        return;
      }

      console.log(queryResults);
      if (queryResults.length > 0) {
        json.chart = "loading";
        json.queryResults = queryResults;
      }

      console.log(json);

      // Update the streaming answer with complete data
      setAnswers((prevState) => {
        const newState = [...prevState];
        for (let i = newState.length - 1; i >= 0; i--) {
          if (newState[i].isStreaming) {
            newState[i] = {
              ...json,
              isStreaming: false, // Mark as no longer streaming
            };
            break;
          }
        }
        return newState;
      });

      setLoading(false);

      if (queryResults.length > 0) {
        json.chart = await generateChart(json, setDashboardEnabled);

        if (!isMounted.current) {
          console.log("No moreeeee,...");
          return;
        }

        console.log("--------- Answer after chart generation ------");
        console.log(json);

        // Update again with chart data
        setAnswers((prevState) => {
          const newState = [...prevState];
          for (let i = newState.length - 1; i >= 0; i--) {
            if (newState[i].queryUuid === queryUuid) {
              newState[i] = {
                ...json,
                isStreaming: false,
              };
              break;
            }
          }
          return newState;
        });

        setTotalAnswers((prevState) => prevState + 1);
      } else {
        if (!isMounted.current) {
          console.log("No moreeeee,...");
          return;
        }

        console.log("------- Answer without chart-------");
        console.log(json);
        setTotalAnswers((prevState) => prevState + 1);
      }
    } catch (error) {
      console.log("Call failed: ", error);
      setErrorMessage(error.toString());
      setLoading(false);

      // Update the streaming answer with error state
      setAnswers((prevState) => {
        const newState = [...prevState];
        for (let i = newState.length - 1; i >= 0; i--) {
          if (newState[i].isStreaming) {
            newState[i] = {
              ...newState[i],
              text: "Error occurred while getting response",
              isStreaming: false,
              error: true,
            };
            break;
          }
        }
        return newState;
      });
    }
  }
};

  const handleClickOpenAnswerDetails = (value) => () => {
    setSelectedAB(value);
    setOpenAnswerDetails(true);
  };

  const handleCloseAnswerDetails = () => {
    setOpenAnswerDetails(false);
  };

  const handleClickOpenBrain = (value) => () => {
    setSelectedAB(value);
    setOpenBrain(true);
  };

  const handleCloseBrain = () => {
    setOpenBrain(false);
  };

  const handleExample = (value) => {
    getAnswer(value);
  };

  const handleShowTab = (index, type) => () => {
    const updatedItems = [...controlAnswers];
    updatedItems[index] = { ...updatedItems[index], current_tab_view: type };
    setControlAnswers(updatedItems);
  };

  return (
    <Box>
      <Grow
        in={true}
        timeout={{ enter: 600, exit: 300 }}
        style={{ transformOrigin: "50% 50% 0" }} // Center origin
        mountOnEnter
        unmountOnExit
      >
        <Box sx={{ p: 0 }}>
          {errorMessage !== "" && (
            <Alert
              severity="error"
              sx={{
                position: "fixed",
                width: "80%",
                top: "65px",
                left: "20%",
                marginLeft: "-10%" /* Negative half of width. */,
                borderRadius: borderRadius / 2,
              }}
              onClose={() => {
                setErrorMessage("");
              }}
            >
              {errorMessage}
            </Alert>
          )}

          <Box
            id="chatHelper"
            sx={{
              display: "flex",
              flexDirection: "column",
              height: height,
              overflow: "hidden",
              overflowY: "scroll",
            }}
          >
            {answers.length > 0 ? (
              /* CHAT */
              <ul
                style={{ paddingBottom: 14, margin: 0, listStyleType: "none" }}
              >
                {answers.map((answer, index) => (
                  <li key={"meg" + index} style={{ marginBottom: 0 }}>
                    {answer.hasOwnProperty("text") ? (
                      <Box
                        sx={{
                          borderRadius: borderRadius,
                          pl: 1,
                          pr: 1,
                          display: "flex",
                          alignItems: "flex-start",
                          marginBottom: 1,
                        }}
                      >
                        <Box sx={{ pr: 1, pt: 1.5, pl: 0.5 }}>
                          <Tooltip
                            title={
                              "Input Tokens: " +
                              answer.totalInputTokens +
                              " - Output Tokens: " +
                              answer.totalOutputTokens
                            }
                          >
                            <IconButton
                              data-amplify-analytics-on="click"
                              data-amplify-analytics-name="click"
                              data-amplify-analytics-attrs="button:brain"
                              aria-label="Sample questions"
                              aria-controls="menu-appbar"
                              aria-haspopup="true"
                              onClick={handleClickOpenBrain(index)}
                              sx={{ p: 0 }}
                            >
                              <img
                                src="/images/genai.png"
                                width={28}
                                height={28}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Box sx={{ p: 0, flex: 1 }}>
                          <Box>
                            <Grow
                              in={
                                controlAnswers[index].current_tab_view ===
                                "answer"
                              }
                              timeout={{ enter: 600, exit: 0 }}
                              style={{ transformOrigin: "20% 0 0" }}
                              mountOnEnter
                              unmountOnExit
                            >
                              <Box
                                id={"answer" + index}
                                sx={{
                                  opacity: 0.8,
                                  "&.MuiBox-root": {
                                    animation:
                                      "fadeIn 0.8s ease-in-out forwards",
                                  },
                                  mt: 1,
                                }}
                              >
                                <Typography component="div" variant="body1">
                                  <MarkdownRenderer content={answer.text} />
                                </Typography>
                              </Box>
                            </Grow>

                            {/* Rest of the tab content rendering logic */}
                            {answer.hasOwnProperty("queryResults") && (
                              <Grow
                                in={
                                  controlAnswers[index].current_tab_view ===
                                  "records"
                                }
                                timeout={{ enter: 600, exit: 0 }}
                                style={{ transformOrigin: "20% 0 0" }}
                                mountOnEnter
                                unmountOnExit
                              >
                                <Box
                                  sx={{
                                    opacity: 0.8,
                                    "&.MuiBox-root": {
                                      animation:
                                        "fadeIn 0.8s ease-in-out forwards",
                                    },
                                    transform: "translateY(10px)",
                                    "&.MuiBox-root-appear": {
                                      transform: "translateY(0)",
                                    },
                                    mt: 1,
                                  }}
                                >
                                  <QueryResultsDisplay
                                    index={index}
                                    answer={answer}
                                  />
                                </Box>
                              </Grow>
                            )}

                            {answer.hasOwnProperty("chart") &&
                              answer.chart.hasOwnProperty("chart_type") && (
                                <Grow
                                  in={
                                    controlAnswers[index].current_tab_view ===
                                    "chart"
                                  }
                                  timeout={{ enter: 600, exit: 0 }}
                                  style={{ transformOrigin: "20% 0 0" }}
                                  mountOnEnter
                                  unmountOnExit
                                >
                                  <Box
                                    sx={{
                                      opacity: 0.8,
                                      "&.MuiBox-root": {
                                        animation:
                                          "fadeIn 0.9s ease-in-out forwards",
                                      },
                                      transform: "translateY(10px)",
                                      "&.MuiBox-root-appear": {
                                        transform: "translateY(0)",
                                      },
                                      mt: 1,
                                    }}
                                  >
                                    <MyChart
                                      caption={answer.chart.caption}
                                      options={
                                        answer.chart.chart_configuration.options
                                      }
                                      series={
                                        answer.chart.chart_configuration.series
                                      }
                                      type={answer.chart.chart_type}
                                    />
                                  </Box>
                                </Grow>
                              )}
                          </Box>

                          {/* Tab buttons and other UI controls */}
                          {answer.hasOwnProperty("queryResults") && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                gap: 1,
                                py: 1,
                                mt: 1,
                              }}
                            >
                              <Button
                                data-amplify-analytics-on="click"
                                data-amplify-analytics-name="click"
                                data-amplify-analytics-attrs="button:answer-details"
                                size="small"
                                color="primary"
                                variant="outlined"
                                onClick={handleClickOpenAnswerDetails(index)}
                                startIcon={<PsychologyRoundedIcon />}
                                sx={{
                                  //borderTop: 'none',
                                  borderRadius: 8,
                                  borderLeft: (theme) =>
                                    `1px solid ${theme.palette.primary.main}20`, // 50 is hex for 0.5 opacity
                                  borderRight: (theme) =>
                                    `1px solid ${theme.palette.primary.main}20`,
                                  borderBottom: (theme) =>
                                    `1px solid ${theme.palette.primary.main}20`,
                                  borderTop: (theme) =>
                                    `1px solid ${theme.palette.primary.main}20`,
                                }}
                              >
                                Details
                              </Button>

                              {answer.queryResults.length > 0 && (
                                <Fade
                                  timeout={1000}
                                  in={answer.queryResults.length > 0}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Button
                                      sx={(theme) => ({
                                        pr: 1,
                                        pl: 1,
                                        "&.Mui-disabled": {
                                          borderBottom: 3,
                                          color: theme.palette.primary.main,
                                          borderRadius: 0,
                                        },
                                      })}
                                      data-amplify-analytics-on="click"
                                      data-amplify-analytics-name="click"
                                      data-amplify-analytics-attrs="button:answer"
                                      size="small"
                                      color="secondaryText"
                                      disabled={
                                        controlAnswers[index]
                                          .current_tab_view === "answer"
                                      }
                                      onClick={handleShowTab(index, "answer")}
                                      startIcon={<QuestionAnswerOutlinedIcon />}
                                    >
                                      Answer
                                    </Button>

                                    <Button
                                      sx={(theme) => ({
                                        pr: 1,
                                        pl: 1,
                                        "&.Mui-disabled": {
                                          borderBottom: 3,
                                          color: theme.palette.primary.main,
                                          borderRadius: 0,
                                        },
                                      })}
                                      data-amplify-analytics-on="click"
                                      data-amplify-analytics-name="click"
                                      data-amplify-analytics-attrs="button:records"
                                      size="small"
                                      color="secondaryText"
                                      disabled={
                                        controlAnswers[index]
                                          .current_tab_view === "records"
                                      }
                                      onClick={handleShowTab(index, "records")}
                                      startIcon={<TableRowsRoundedIcon />}
                                    >
                                      Records
                                    </Button>

                                    {typeof answer.chart === "object" &&
                                      answer.chart.hasOwnProperty(
                                        "chart_type"
                                      ) && (
                                        <Button
                                          sx={(theme) => ({
                                            pr: 1,
                                            pl: 1,
                                            "&.Mui-disabled": {
                                              borderBottom: 3,
                                              color: theme.palette.primary.main,
                                              borderRadius: 0,
                                            },
                                          })}
                                          data-amplify-analytics-on="click"
                                          data-amplify-analytics-name="click"
                                          data-amplify-analytics-attrs="button:chart"
                                          size="small"
                                          color="secondaryText"
                                          disabled={
                                            controlAnswers[index]
                                              .current_tab_view === "chart"
                                          }
                                          onClick={handleShowTab(
                                            index,
                                            "chart"
                                          )}
                                          startIcon={<InsightsOutlinedIcon />}
                                        >
                                          Chart
                                        </Button>
                                      )}
                                  </Box>
                                </Fade>
                              )}

                              {answer.chart === "loading" && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    ml: 1,
                                  }}
                                >
                                  <CircularProgress size={16} color="primary" />
                                  <Typography
                                    variant="caption"
                                    color="secondaryText"
                                    sx={{ ml: 1 }}
                                  >
                                    Generating chart...
                                  </Typography>
                                </Box>
                              )}

                              {answer.chart.hasOwnProperty("rationale") && (
                                <Typography
                                  variant="caption"
                                  color="secondaryText"
                                >
                                  {answer.chart.rationale}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    ) : answer.hasOwnProperty("rationaleText") ? (
                      <Grid container justifyContent="flex-start">
                        <Fade timeout={2000} in={true}>
                          <Box
                            sx={{
                              borderRadius: borderRadius,
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                              boxShadow: "rgba(0, 0, 0, 0.05) 0px 1px 2px 0px;",
                              pl: 2,
                              py: 1,
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <img
                                src="/images/sun.256x256.png"
                                width={22}
                                height={22}
                                style={{ opacity: 0.4 }}
                              />
                            </Box>

                            <Box
                              sx={{
                                pl: 0.5,
                                pr: 2,
                                ml: 1,
                                display: "flex",
                                alignItems: "center",
                                flexGrow: 1,
                              }}
                            >
                              <Typography
                                color="text.secondary"
                                variant="body1"
                              >
                                <ChatStreamSimulator
                                  message={answer.rationaleText}
                                  typingSpeed={12}
                                />
                              </Typography>
                            </Box>
                          </Box>
                        </Fade>
                      </Grid>
                    ) : (
                      <Grid container justifyContent="flex-end">
                        <Box
                          sx={(theme) => ({
                            textAlign: "right",
                            borderRadius: borderRadius,
                            fontWeight: 500,
                            pt: 1,
                            pb: 1,
                            pl: 2,
                            pr: 2,
                            mt: index === 0 ? 10 : 2,
                            mb: 1.5,
                            mr: 1,
                            border: "1px solid",
                            borderColor:
                              theme.palette.mode === "light"
                                ? "rgba(0, 0, 0, 0.1)"
                                : "divider",
                            boxShadow:
                              theme.palette.mode === "light"
                                ? "rgba(0, 0, 0, 0.05) 0px 2px 8px"
                                : "rgba(100, 100, 100, 0.05) 0px 4px 12px",
                            background:
                              theme.palette.mode === "light"
                                ? "rgba(245, 247, 250, 0.6)" // Light, semi-transparent background
                                : "linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.15) 100%)",
                            position: "relative",
                          })}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              position: "relative",
                              zIndex: 1,
                              color: "text.primary",
                              fontWeight: 500,
                            }}
                          >
                            {answer.query}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </li>
                ))}

                {loading && (
                  <Box sx={{ p: 0, pl: 1, mb: 2, mt: 1 }}>
                    <Answering loading={loading} />
                  </Box>
                )}

                <li ref={scrollRef} />
              </ul>
            ) : (
              <ChatWelcomeView
                height={height}
                assistant={assistant}
                language={language}
                handleExample={handleExample}
              />
            )}
          </Box>

          <InputQuery
            onSubmitQuery={getAnswer}
            isLoading={loading}
            suggestionQuestions={assistant.language_questions}
            infoSuggestionQuestions={assistant.info_language_questions}
            language={language}
            borderRadius={borderRadius}
          />

          <Box
            sx={{
              textAlign: "center",
              mt: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 500, opacity: 0.5, textAlign: "center" }}
            >
              {
                assistant.communication_styles[
                  assistant.selected_communication_style
                ].name
              }{" "}
              - {assistant.models[assistant.selected_model].name} Session
              Tokens: <strong>Input:</strong>{" "}
              {grandTotalInputTokens.toLocaleString()} |{" "}
              <strong>Output:</strong> {grandTotalOutputTokens.toLocaleString()}
            </Typography>
          </Box>

          {selectedAB > 0 && (
            <AnswerDetailsDialog
              open={openAnswerDetails}
              handleClose={handleCloseAnswerDetails}
              answer={answers[selectedAB]}
              question={
                answers[selectedAB - (answers[selectedAB].countRationals + 1)]
                  .query
              }
            />
          )}

          {selectedAB > 0 && (
            <BrainDialog
              open={openBrain}
              handleClose={handleCloseBrain}
              usage={answers[selectedAB].usage}
              question={
                answers[selectedAB - (answers[selectedAB].countRationals + 1)]
                  .query
              }
              totalInputTokens={answers[selectedAB].totalInputTokens}
              totalOutputTokens={answers[selectedAB].totalOutputTokens}
              runningTraces={answers[selectedAB].runningTraces}
            />
          )}
        </Box>
      </Grow>
    </Box>
  );
};

export default Chat;
